import { Alert, Box, DialogContent } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { addDays, isBefore } from 'date-fns';
import React, { ReactNode, useEffect, useLayoutEffect } from 'react';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';
import AttemptsField from '../../forms/attempts-field/AttemptsField';
import DescriptionField from '../../forms/description-field/DescriptionField';
import EndTimeField from '../../forms/end-time-field/EndTimeField';
import StartTimeField from '../../forms/start-time-field/StartTimeField';
import TimezoneField from '../../forms/timezone-field/TimezoneField';
import TitleField from '../../forms/title-field/TitleField';
import KSimHeader from '../../k-sim-header/KSimHeader';
import { ExerciseType, Timezone } from '../../types';
import { useSelectUserTimeZone } from '../../utils/useSelectUserTimeZone';
import { ExerciseFormData } from './CreateExerciseDialog';

export type OverviewStepProps = UseFormReturn<ExerciseFormData> & {
  actions: ReactNode;
  handleNext: SubmitHandler<ExerciseFormData>;
  stepper: ReactNode;
};

const OverviewStep = ({
  actions,
  clearErrors,
  control,
  handleNext,
  handleSubmit,
  setError,
  setValue,
  stepper,
  trigger,
  watch,
}: OverviewStepProps) => {
  const { data: timezones, isError } = useQuery<Timezone[]>({ queryKey: [{ path: 'time-zones' }] });

  useSelectUserTimeZone<ExerciseFormData>(setValue, timezones);

  // Revalidate start/end time when start/end time or timezone changes
  const [timezone, startTime, endTime, limitNumberOfAttempts] = watch([
    'timezone',
    'startTime',
    'endTime',
    'limitNumberOfAttempts',
  ]);

  useLayoutEffect(() => {
    trigger(['startTime', 'endTime']);
    if (startTime !== null && endTime !== null && isBefore(endTime, startTime)) {
      setError('timeRange', { message: 'Start time should be before end time' });
    } else {
      clearErrors('timeRange');
    }
  }, [timezone, startTime, endTime]);
  const selectedTimezone = timezones?.find(tz => tz.id === timezone);

  useEffect(() => {
    if (startTime && !endTime) {
      setValue('endTime', addDays(startTime, 1));
    }
  }, [startTime]);

  useEffect(() => {
    if (!limitNumberOfAttempts) {
      clearErrors('maxNumberOfAttempts');
    }
  }, [limitNumberOfAttempts]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(handleNext)}
      noValidate
      autoComplete="off"
      sx={{ display: 'contents' }}
    >
      <DialogContent>
        {stepper}
        <Box display="flex" flexDirection="column" gap={2}>
          <KSimHeader exerciseType={watch('type')} mobileOnly sx={{ height: '153px' }} />

          <TitleField control={control} name="title" />

          <DescriptionField control={control} name="description" />

          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
            <StartTimeField control={control} name="startTime" />
            <EndTimeField control={control} name="endTime" timezone={selectedTimezone} />
          </Box>

          <TimezoneField control={control} name="timezone" timezones={timezones ?? []} />

          {watch('type') === ExerciseType.SelfStudy && <AttemptsField control={control} name="maxNumberOfAttempts" />}
          {isError && <Alert severity="error">An issue prevented the list of timezones to be fetched</Alert>}
        </Box>
      </DialogContent>
      {actions}
    </Box>
  );
};

export default OverviewStep;
